import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  Car,
  Briefcase,
  CalendarClock,
  Flame,
  Trophy,
  Star,
  ChevronRight,
  Settings,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProgressiveChecklist from "../checks/ProgressiveChecklist";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * InstructorChecksWidget — sits on the instructor dashboard.
 *  - Daily Status: Vehicle Ready ✅ · Business Ready ✅ (one-tap launch)
 *  - Weekly Status: Vehicle compliance % · Weekly Review status
 *  - Streak badges (7-day fire, 30-day trophy, 100 elite)
 *  - "Reserve weekly review slot" CTA the day before
 */
export default function InstructorChecksWidget() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [catalogue, setCatalogue] = useState({});
  const [activeCheck, setActiveCheck] = useState(null);
  const [reserving, setReserving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, r, c] = await Promise.all([
        axios.get(`${API}/api/checks/dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/checks/weekly-reminder`, { withCredentials: true }),
        axios.get(`${API}/api/checks/catalogue`, { withCredentials: true }),
      ]);
      setData(d.data);
      setReminder(r.data);
      const cat = (c.data.checks || []).reduce((a, x) => {
        a[x.key] = x;
        return a;
      }, {});
      setCatalogue(cat);
    } catch {
      /* hide silently — instructor still sees rest of dashboard */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const launch = (checkKey) => {
    if (catalogue[checkKey]) setActiveCheck(catalogue[checkKey]);
  };

  const submitCheck = async ({ skipped, answers }) => {
    if (!activeCheck) return;
    try {
      await axios.post(
        `${API}/api/checks/submit`,
        { check_key: activeCheck.key, skipped, answers },
        { withCredentials: true }
      );
    } catch {
      /* silent */
    }
  };

  const reserveSlot = async () => {
    setReserving(true);
    try {
      const { data: r } = await axios.post(
        `${API}/api/checks/reserve-weekly-slot`,
        {},
        { withCredentials: true }
      );
      toast.success(`Reserved ${r.slot_date} at ${r.slot_time}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reserve");
    } finally {
      setReserving(false);
    }
  };

  if (!data) return null;

  const dvDone = data.today.daily_vehicle.completed;
  const dbDone = data.today.daily_business.completed;
  const wvDone = data.weekly.weekly_vehicle.completed;
  const wbDone = data.weekly.weekly_business.completed;
  const compliance = data.weekly.vehicle_compliance_pct ?? 0;
  const streakV = data.streaks.daily_vehicle.streak;
  const streakB = data.streaks.daily_business.streak;
  const allBadges = [
    ...(data.streaks.daily_vehicle.badges || []),
    ...(data.streaks.daily_business.badges || []),
  ];

  return (
    <>
      <div
        data-testid="instructor-checks-widget"
        className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 p-4 text-white"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-400">
              Today's readiness
            </p>
            <p className="text-[12px] text-slate-300">
              30 seconds of focus before you teach.
            </p>
          </div>
          {allBadges.length > 0 && (
            <div className="flex gap-1">
              {allBadges.slice(0, 3).map((b) => (
                <BadgePill key={b.key} badge={b} />
              ))}
            </div>
          )}
          <button
            data-testid="checks-open-standards"
            onClick={() => nav("/instructor/standards")}
            aria-label="Operating Standards"
            className="p-1.5 rounded-lg hover:bg-slate-800 text-amber-400 hover:text-amber-300"
          >
            <Trophy className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="checks-prefs-btn"
            onClick={() => nav("/instructor/weekly-review-prefs")}
            aria-label="Weekly review settings"
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-slate-300"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <StatusButton
            testid="checks-tile-daily-vehicle"
            icon={Car}
            label="Vehicle"
            done={dvDone}
            streak={streakV}
            onClick={() => launch("daily_vehicle")}
          />
          <StatusButton
            testid="checks-tile-daily-business"
            icon={Briefcase}
            label="Business"
            done={dbDone}
            streak={streakB}
            onClick={() => launch("daily_business")}
          />
        </div>

        <div className="mt-3 pt-3 border-t border-slate-800">
          <div className="flex items-center justify-between text-[11px] mb-2">
            <p className="font-extrabold uppercase tracking-wider text-violet-400">
              This week
            </p>
            <p data-testid="checks-compliance" className="font-extrabold text-slate-200">
              {compliance}% compliance
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <SmallButton
              testid="checks-tile-weekly-vehicle"
              label="Vehicle standards"
              done={wvDone}
              onClick={() => launch("weekly_vehicle")}
            />
            <SmallButton
              testid="checks-tile-weekly-business"
              label="Weekly review"
              done={wbDone}
              onClick={() => launch("weekly_business")}
            />
          </div>
        </div>

        {reminder?.due_tomorrow && !reminder?.already_reserved && (
          <div
            data-testid="checks-weekly-reminder"
            className="mt-3 pt-3 border-t border-slate-800 flex items-start gap-3"
          >
            <CalendarClock className="w-4 h-4 text-amber-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-extrabold">
                Your weekly review is due tomorrow.
              </p>
              <p className="text-[11px] text-slate-400">
                {reminder.next_review_day_label} · {reminder.next_review_time}.
                Block the slot so nothing else lands in it.
              </p>
            </div>
            <button
              data-testid="checks-reserve-slot"
              onClick={reserveSlot}
              disabled={reserving}
              className="px-3 py-1.5 text-[11px] font-extrabold rounded-full bg-amber-500 text-slate-900 hover:bg-amber-400 disabled:opacity-60"
            >
              Reserve
            </button>
          </div>
        )}

        {reminder?.already_reserved && (
          <p
            data-testid="checks-reserved-confirm"
            className="mt-3 pt-3 border-t border-slate-800 text-[11px] text-emerald-400"
          >
            Weekly review reserved · {reminder.next_review_date} at {reminder.next_review_time}
          </p>
        )}
      </div>

      {activeCheck && (
        <ProgressiveChecklist
          check={activeCheck}
          onComplete={submitCheck}
          onClose={() => {
            setActiveCheck(null);
            load();
          }}
          onCta={() => {
            setActiveCheck(null);
            load();
          }}
        />
      )}
    </>
  );
}

function StatusButton({ testid, icon: Icon, label, done, streak, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative w-full text-left rounded-xl p-3 border transition ${
        done
          ? "bg-emerald-600/15 border-emerald-600/50 hover:border-emerald-500"
          : "bg-slate-800 border-slate-700 hover:border-violet-500"
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            done ? "bg-emerald-600 text-white" : "bg-slate-700 text-slate-300"
          }`}
        >
          {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-extrabold truncate">
            {label}{" "}
            <span className={done ? "text-emerald-400" : "text-slate-400"}>
              {done ? "Ready" : "Pending"}
            </span>
          </p>
          {streak > 0 && (
            <p className="text-[10px] text-amber-400 inline-flex items-center gap-0.5">
              <Flame className="w-3 h-3" /> {streak}-day streak
            </p>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500" />
      </div>
    </button>
  );
}

function SmallButton({ testid, label, done, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full text-left rounded-xl px-3 py-2 border transition flex items-center gap-2 ${
        done
          ? "bg-emerald-600/15 border-emerald-600/50 text-emerald-200"
          : "bg-slate-800 border-slate-700 text-slate-300 hover:border-violet-500"
      }`}
    >
      {done ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
      ) : (
        <Circle className="w-3.5 h-3.5 text-slate-500" />
      )}
      <span className="text-[11px] font-bold flex-1 truncate">{label}</span>
      <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
    </button>
  );
}

function BadgePill({ badge }) {
  const Icon =
    badge.key === "fire_7" ? Flame : badge.key === "trophy_30" ? Trophy : Star;
  const tone =
    badge.key === "fire_7"
      ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
      : badge.key === "trophy_30"
      ? "bg-violet-500/15 text-violet-300 border-violet-500/40"
      : "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40";
  return (
    <div
      data-testid={`badge-${badge.key}`}
      title={badge.label}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-extrabold ${tone}`}
    >
      <Icon className="w-3 h-3" /> {badge.label}
    </div>
  );
}
